import { useEffect, useState, useRef } from "react";
import { useApiKeys, useApiKeyUsageSummary, useApiKeyUsageDashboard, useUpdateApiKey } from "../hooks/useApi";
import Button from "../components/Button";
import Badge from "../components/Badge";
import KeySpendBarChart from "../components/KeySpendBarChart";
import { formatTomanInt, parsePersianNumber, toPersianDigits, normalizeDigits } from "../utils/format";
import type { DashboardUsageData } from "../components/StackedAreaChart";

interface KeyDetailDrawerProps {
  selectedKey: Record<string, unknown> | null;
  onClose: () => void;
}

/** Relative Persian time string (e.g., "۲ ساعت پیش", "دیروز") */
function relativeTime(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "همین الان";
  if (diffMin < 60) return `${diffMin.toLocaleString("fa-IR")} دقیقه پیش`;
  if (diffHour < 24) return `${diffHour.toLocaleString("fa-IR")} ساعت پیش`;
  if (diffDay < 2) return "دیروز";
  if (diffDay < 7) return `${diffDay.toLocaleString("fa-IR")} روز پیش`;
  return new Date(dateStr).toLocaleDateString("fa-IR");
}

function formatCreated(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("fa-IR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

interface StatRowProps {
  label: string;
  value: string;
}

function StatRow({ label, value }: StatRowProps) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-small text-text-secondary">{label}</span>
      <span className="text-body font-number font-medium text-text-main">{value}</span>
    </div>
  );
}

interface BoxProps {
  title: string;
  children: React.ReactNode;
}

function Box({ title, children }: BoxProps) {
  return (
    <div className="card p-4 flex flex-col">
      <h3 className="text-h3 font-bold text-text-main mb-3">{title}</h3>
      <div className="flex-1">{children}</div>
    </div>
  );
}

export default function KeyDetailDrawer({ selectedKey, onClose }: KeyDetailDrawerProps) {
  const { data: keys } = useApiKeys();
  const updateKey = useUpdateApiKey();

  const [nameInput, setNameInput] = useState<string>("");
  const backdropMouseDown = useRef(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [disableConfirmText, setDisableConfirmText] = useState("");
  const [limitInput, setLimitInput] = useState<string>("");
  const [infoTooltipSide, setInfoTooltipSide] = useState<"left" | "right">("right");

  const keyId = selectedKey ? (selectedKey.id as string | number) : null;

  // Use the freshest key from the list so status/name updates reflect live.
  const liveKey =
    keys?.find((k: Record<string, unknown>) => k.id === keyId) || selectedKey || null;

  const isActive = liveKey?.status === "active";
  const disableKeyName = (liveKey?.name as string) || "بدون نام";
  const canDisable = disableConfirmText.trim() === disableKeyName;

  const { data: summary, isLoading: summaryLoading } = useApiKeyUsageSummary(keyId);
  const { data: chartData, isLoading: chartLoading } = useApiKeyUsageDashboard(keyId, "15d", "spend");

  useEffect(() => {
    if (selectedKey) {
      setNameInput((selectedKey.name as string) || "");
      const limitIrl = Number(selectedKey.credit_limit) || 0;
      setLimitInput(limitIrl > 0 ? String(Math.round(limitIrl / 10)) : "");
      setConfirmDisable(false);
      setDisableConfirmText("");
    }
  }, [selectedKey]);

  useEffect(() => {
    if (selectedKey) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [selectedKey]);

  if (!selectedKey) return null;

  const handleRename = () => {
    const name = nameInput.trim();
    if (!keyId) return;
    if (name === ((liveKey?.name as string) || "")) return;
    updateKey.mutate({ id: keyId, data: { name } });
  };

  const handleDisable = () => {
    if (!keyId) return;
    updateKey.mutate(
      { id: keyId, data: { status: "disabled" } },
      { onSuccess: () => setConfirmDisable(false) }
    );
  };

  const currentLimitIrl = Number(liveKey?.credit_limit) || 0;
  const spentIrl = Number(liveKey?.spent) || 0;
  const limitPct =
    currentLimitIrl > 0 ? Math.min(100, Math.round((spentIrl / currentLimitIrl) * 100)) : 0;

  const handleSetLimit = () => {
    if (!keyId) return;
    const toman = parsePersianNumber(limitInput);
    updateKey.mutate({
      id: keyId,
      data: { credit_limit: toman > 0 ? Math.round(toman * 10) : 0 },
    });
  };

  const handleClearLimit = () => {
    if (!keyId) return;
    setLimitInput("");
    updateKey.mutate({ id: keyId, data: { credit_limit: 0 } });
  };

  // Pick the tooltip side (left/right of the info icon) with more room.
  const handleInfoTooltip = (e: React.MouseEvent<HTMLSpanElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceLeft = rect.left;
    const spaceRight = window.innerWidth - rect.right;
    setInfoTooltipSide(spaceRight >= spaceLeft ? "right" : "left");
  };

  const totalSpend = formatTomanInt(summary?.total_cost);
  const todaySpend = formatTomanInt(summary?.today_cost);
  const weekSpend = formatTomanInt(summary?.week_cost);
  const monthSpend = formatTomanInt(summary?.month_cost);

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm animate-fade-in"
        onMouseDown={(e) => { backdropMouseDown.current = e.target === e.currentTarget; }}
        onClick={(e) => { if (e.target === e.currentTarget && backdropMouseDown.current) onClose(); backdropMouseDown.current = false; }}
      />

      {/* Drawer panel — slides in from the left, fills half the page width */}
      <aside
        className="absolute top-0 bottom-0 left-0 w-full md:w-1/2 bg-surface-base border-r border-line shadow-[0_20px_60px_rgba(0,0,0,0.15)] flex flex-col overflow-hidden animate-drawer-left"
      >
        {/* Header */}
        <div className="bg-surface px-6 pt-5 pb-4 border-b border-line">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-h2 text-text-main truncate">
                  {liveKey?.name || "بدون نام"}
                </h2>
                <Badge variant={isActive ? "success" : "error"}>
                  {isActive ? "فعال" : "غیرفعال"}
                </Badge>
              </div>
              <p className="font-mono text-small text-text-tertiary ltr text-left mt-1.5 tracking-tight">
                {(liveKey?.key_prefix as string) || ""}
              </p>
              <p className="text-small text-text-tertiary mt-1">
                ساخته‌شده در:{" "}
                <span className="font-number text-text-secondary">
                  {liveKey ? formatCreated(liveKey.created_at as string) : "—"}
                </span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-text-tertiary hover:text-text-main transition-colors p-1 shrink-0"
              aria-label="بستن"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body: three boxes in a single row on wide screens */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
            {/* Box 1: Main settings */}
            <Box title="تنظیمات اصلی">
              <div className="space-y-4">
                <div>
                  <label className="block text-small font-medium text-text-secondary mb-1.5">
                    نام کلید
                  </label>
                  <input
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRename();
                    }}
                    disabled={!isActive}
                    className="w-full h-9 px-3 rounded-btn border border-line text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="بدون نام"
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full"
                  onClick={handleRename}
                  loading={updateKey.isPending}
                  disabled={!isActive || nameInput.trim() === ((liveKey?.name as string) || "")}
                >
                  ذخیره نام
                </Button>

                <div className="pt-3 border-t border-line">
                  {isActive ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-full !text-status-error-text !border-status-error-text/30 hover:!bg-status-error-bg"
                      onClick={() => {
                        setDisableConfirmText("");
                        setConfirmDisable(true);
                      }}
                    >
                      غیرفعال کردن کلید
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 bg-surface-hover rounded-btn px-3 py-2.5">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-status-error-text shrink-0">
                        <rect x="3" y="11" width="18" height="11" rx="2" />
                        <path d="M7 11V7a5 5 0 0110 0v4" />
                      </svg>
                      <span className="text-small text-text-secondary">
                        این کلید غیرفعال است و نمی‌توان آن را دوباره فعال کرد.
                      </span>
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-line">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-small font-medium text-text-secondary">سقف هزینه</span>
                    {currentLimitIrl > 0 ? (
                      <span className="text-small font-number text-text-main">
                        {formatTomanInt(spentIrl)} از {formatTomanInt(currentLimitIrl)} تومان
                      </span>
                    ) : (
                      <span className="text-small text-text-tertiary">نامحدود</span>
                    )}
                  </div>

                  {currentLimitIrl > 0 && (
                    <div className="h-1.5 rounded-full bg-surface-hover overflow-hidden mb-3">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${limitPct}%`,
                          background: limitPct >= 100 ? "rgb(var(--status-error-text))" : "rgb(var(--color-primary))",
                        }}
                      />
                    </div>
                  )}

                  <div className="flex gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      dir="ltr"
                      value={toPersianDigits(limitInput)}
                      onChange={(e) => setLimitInput(normalizeDigits(e.target.value))}
                      onKeyDown={(e) => { if (e.key === "Enter") handleSetLimit(); }}
                      disabled={!isActive}
                      className="flex-1 min-w-0 h-9 px-3 rounded-btn border border-line text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="مقدار سقف (تومان)"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      className="shrink-0"
                      onClick={handleSetLimit}
                      loading={updateKey.isPending}
                      disabled={!isActive}
                    >
                      ذخیره
                    </Button>
                  </div>

                  <div className="flex items-start justify-between gap-2 mt-2">
                    <p className="text-[11px] text-text-tertiary leading-relaxed">
                      پس از رسیدن هزینه‌ی کلید به این سقف، درخواست‌های جدید با این کلید
                      رد می‌شوند تا سقف را افزایش دهید یا آن را بردارید.
                    </p>
                    {currentLimitIrl > 0 && (
                      <button
                        onClick={handleClearLimit}
                        disabled={!isActive}
                        className="shrink-0 text-small text-status-error-text hover:underline disabled:opacity-50 transition-colors"
                      >
                        حذف سقف
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Box>

            {/* Box 2: Usage detail */}
            <Box title="جزئیات مصرف">
              <div className="space-y-0.5">
                <StatRow label="کل" value={summaryLoading ? "…" : `${totalSpend} تومان`} />
                <StatRow label="امروز" value={summaryLoading ? "…" : `${todaySpend} تومان`} />
                <StatRow label="این هفته" value={summaryLoading ? "…" : `${weekSpend} تومان`} />
                <StatRow label="این ماه" value={summaryLoading ? "…" : `${monthSpend} تومان`} />
              </div>

              <div className="mt-3 pt-3 border-t border-line space-y-0.5">
                <StatRow
                  label="ساخته‌شده"
                  value={liveKey ? relativeTime(liveKey.created_at as string) : "—"}
                />
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-small text-text-secondary">آخرین استفاده</span>
                  <span className="flex items-center gap-1.5">
                    <span className="text-body font-number font-medium text-text-main">
                      {liveKey?.last_used_at
                        ? relativeTime(liveKey.last_used_at as string)
                        : "هنوز استفاده نشده"}
                    </span>
                    <span className="relative inline-flex group" onMouseEnter={handleInfoTooltip}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        className="text-text-tertiary cursor-help"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                      <span
                        className={`absolute top-1/2 -translate-y-1/2 hidden group-hover:block z-30 w-52 px-3 py-2 rounded-btn text-[11px] leading-relaxed whitespace-normal pointer-events-none ${
                          infoTooltipSide === "right" ? "left-full ml-2" : "right-full mr-2"
                        }`}
                        style={{ background: "rgba(26,26,26,0.92)", color: "#fff", boxShadow: "0 8px 24px rgba(0,0,0,0.18)" }}
                      >
                        آخرین باری که این کلید در یک درخواست استنتاج با موفقیت استفاده شده است.
                      </span>
                    </span>
                  </span>
                </div>
              </div>
            </Box>

            {/* Box 3: Total spend + recent-days cumulative chart */}
            <Box title="هزینه کل و روند روزهای اخیر">
              <div className="mb-3">
                <p className="text-small text-text-tertiary mb-1">هزینه کل</p>
                <p className="text-h1 font-semibold text-text-main font-number">
                  {chartLoading ? "…" : formatTomanInt(chartData?.summary?.spend)}
                  <span className="text-base text-text-tertiary font-normal mr-1">تومان</span>
                </p>
              </div>
              <KeySpendBarChart data={chartData as DashboardUsageData} loading={chartLoading} />
            </Box>
          </div>
        </div>
      </aside>

      {/* Disable confirmation */}
      {confirmDisable && (
        <div className="absolute inset-0 z-20 flex items-center justify-center p-4">
          <div className="glass rounded-card w-full max-w-md p-6 relative animate-drawer">
            <div className="flex items-start gap-2.5 bg-status-error-bg rounded-btn p-3.5 border border-status-error-text/10 mb-4">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-status-error-text shrink-0 mt-0.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div>
                <p className="text-small font-medium text-status-error-text">آیا از غیرفعال کردن این کلید اطمینان دارید؟</p>
                <p className="text-small text-status-error-text/70 mt-0.5">
                  پس از غیرفعال شدن، این کلید دیگر قابل استفاده نخواهد بود و امکان فعال‌سازی دوباره آن وجود ندارد.
                  برای تأیید، نام کلید <span className="font-medium text-status-error-text">{disableKeyName}</span> را در کادر زیر وارد کنید.
                </p>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-small font-medium text-text-secondary mb-1.5">
                نام کلید
              </label>
              <input
                type="text"
                value={disableConfirmText}
                onChange={(e) => setDisableConfirmText(e.target.value)}
                className="w-full h-9 px-3 rounded-btn border border-line text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                placeholder={disableKeyName}
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setConfirmDisable(false);
                  setDisableConfirmText("");
                }}
              >
                انصراف
              </Button>
              <Button
                className="flex-1 !bg-status-error-text hover:!bg-red-700"
                onClick={handleDisable}
                loading={updateKey.isPending}
                disabled={!canDisable}
              >
                غیرفعال کردن
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
